/* eslint-disable no-nested-ternary */
import React, { useState, useMemo, useRef, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import { useDebouncedCallback } from "use-debounce";
import { ClearButton, Divider, Input, InputActions, InputContent, InputContentText, InputSearch, Label, OptionItem, OptionList, SelectWrapper } from "./styles";

const AsyncSelectV2 = ({
  id,
  name,
  label,
  value,
  defaultOptions = [],
  isMulti = false,
  onChange,
  isClearable = true,
  isDisabled = false,
  isLoading = false,
  placeholder = "Seleccionar...",
  formik,
  height,
  loadOptions,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState(null);
  const [asyncOptions, setAsyncOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const wrapperRef = useRef(null);

  const debouncedSetSearch = useDebouncedCallback((searchValue) => {
    setSearch(searchValue);
  }, 100);

  // Cierra el menú al hacer click fuera del componente
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  // Carga asincrónica de opciones
  useEffect(() => {
    if (typeof loadOptions === "function" && search) {
      setLoadingOptions(true);

      const promise = loadOptions(search);

      if (promise && typeof promise.then === "function") {
        promise
          .then((loaded) => {
            setAsyncOptions(loaded || []);
          })
          .catch((error) => {
            // eslint-disable-next-line no-console
            console.error("Error loading options:", error);
            setAsyncOptions([]);
          })
          .finally(() => {
            setLoadingOptions(false);
          });
      } else {
        setLoadingOptions(false);
      }
    } else {
      // Si no hay search o loadOptions no es función, opcional limpiar opciones o loading
      setAsyncOptions([]);
      setLoadingOptions(false);
    }
  }, [search, loadOptions]);

  const handleOptionClick = (option) => {
    let newValue;
    let action;

    if (isMulti) {
      const exists = value && value.some((v) => v.value === option.value);
      if (exists) {
        newValue = value.filter((v) => v.value !== option.value);
        action = "deselect-option";
      } else {
        newValue = [...(value || []), option];
        action = "select-option";
      }
    } else if (value && value.value === option.value) {
      newValue = null;
      action = "clear";
    } else {
      newValue = option;
      action = "select-option";
    }

    if (formik && name) {
      let fieldValue;
      if (isMulti) {
        fieldValue = newValue;
      } else if (newValue) {
        fieldValue = newValue.value !== undefined ? newValue.value : null;
      } else {
        fieldValue = null;
      }
      formik.setFieldValue(name, fieldValue);
    } else {
      onChange(newValue, {
        action,
        name,
        option: isMulti ? option : undefined,
        removedValues: (isMulti && value && value.filter((v) => v.value === option.value)) || [value],
      });
    }

    setOpen(false);
    setSearch("");
  };

  const handleClear = () => {
    if (isDisabled) return;
    const previous = value;

    if (formik && name) {
      formik.setFieldValue(name, isMulti ? [] : null);
    } else {
      onChange(isMulti ? [] : null, {
        action: "clear",
        name,
        removedValues: isMulti ? previous : [previous],
      });
    }
    setSearch("");
  };

  const isSelected = (option) => (isMulti ? Array.isArray(value) && value.some((v) => v.value === option.value) : value && value.value === option.value);

  let displayValue = "";

  if (isMulti && Array.isArray(value) && value.length > 0) {
    const [first, ...rest] = value;
    displayValue = first.label;
    if (rest.length > 0) {
      displayValue += ` /(+${rest.length})`;
    }
  } else if (!isMulti && value) {
    displayValue = value.label;
  }

  const filteredOptions = useMemo(() => {
    if (typeof loadOptions === "function" && search) return asyncOptions;
    return defaultOptions;
  }, [defaultOptions, search, asyncOptions, loadOptions]);

  return (
    <SelectWrapper ref={wrapperRef}>
      {label && <Label htmlFor={id}>{label}</Label>}
      <Input
        name={name}
        onClick={(e) => {
          if (isDisabled || isLoading) return;

          const isInput = e.target.tagName === "INPUT";
          if (!isInput) {
            e.stopPropagation();
            setOpen((prev) => !prev);
          }
        }}
        height={height}
      >
        {(value && !isMulti && value !== "") || (isMulti && value && value.length && value !== "") ? (
          <InputContent>
            {isMulti ? (
              <>
                <InputContentText>{displayValue.split("/")[0]}</InputContentText>
                <span>{displayValue.split("/")[1]}</span>
              </>
            ) : (
              <InputContentText>{displayValue}</InputContentText>
            )}

            {isClearable && value && (
              <ClearButton
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClear();
                }}
              >
                ✕
              </ClearButton>
            )}
          </InputContent>
        ) : (
          <InputSearch
            type="text"
            name={name}
            id={id}
            value={search}
            onChange={(e) => debouncedSetSearch(e.target.value)}
            placeholder={placeholder}
            disabled={isDisabled || isLoading}
            autoComplete="off"
            open={open}
          />
        )}

        <InputActions>
          <Divider>
            <ChevronDown color="#cccccc" size={16} />
          </Divider>
        </InputActions>
      </Input>

      {open && !isDisabled && (
        <OptionList>
          {loadingOptions || isLoading ? (
            <OptionItem key="loading">Cargando...</OptionItem>
          ) : filteredOptions && filteredOptions.length > 0 ? (
            [...filteredOptions]
              .sort((a, b) => {
                const aSelected = isSelected(a);
                const bSelected = isSelected(b);
                if (aSelected === bSelected) return 0;
                return aSelected ? -1 : 1;
              })
              .map((opt) => (
                <OptionItem key={opt.value} selected={isSelected(opt)} onClick={() => handleOptionClick(opt)}>
                  {opt.label}
                  {isSelected(opt) && <span>✔</span>}
                </OptionItem>
              ))
          ) : (
            <OptionItem key="no-results" onClick={() => setOpen(false)}>
              Sin opciones disponibles
            </OptionItem>
          )}
        </OptionList>
      )}
    </SelectWrapper>
  );
};

export default AsyncSelectV2;
